import { COINS_DISABLED } from '../config/coinsFlag';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, Zap, Loader2, CheckCircle, AlertCircle, Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * CoinPurchasePanel — shared upgrade/payment card used by:
 *   1. CV Builder premium unlock
 *   2. Ad Boost flow
 *
 * Usage:
 *   <CoinPurchasePanel
 *     variant="cv" | "boost"
 *     price={10}
 *     loading={false}
 *     success={false}
 *     onPurchase={() => ...}
 *     onClose={() => ...}
 *     extra={{ adTitle: "..." }}   // boost-only
 *   />
 */

const VARIANTS = {
  cv: {
    icon: Sparkles,
    accentColor: '#059669',
    accentBg: '#f0fdf4',
    accentBorder: '#bbf7d0',
    gradientFrom: '#059669',
    gradientTo: '#047857',
    en: {
      title: 'Unlock Premium CV',
      subtitle: 'Create a professional CV that stands out and increases your chances.',
      benefits: ['Better design', 'Cleaner layout', 'More impact'],
      ctaPrimary: 'Unlock Premium',
      ctaSecondary: 'Get More Coins',
      successTitle: 'Upgrade successful',
      successSub: 'Your premium feature is now active',
      notEnoughTitle: 'Not enough coins',
      notEnoughSub: (missing) => `You need ${missing} more coins to unlock Premium.`,
      unlocking: 'Unlocking...',
    },
    ar: {
      title: 'فتح السيرة الذاتية المميزة',
      subtitle: 'أنشئ سيرة ذاتية احترافية تزيد من فرصك.',
      benefits: ['تصميم أفضل', 'تنسيق أنظف', 'تأثير أقوى'],
      ctaPrimary: 'فتح النسخة المميزة',
      ctaSecondary: 'احصل على عملات',
      successTitle: 'تم التفعيل بنجاح',
      successSub: 'ميزتك المميزة أصبحت مفعلة الآن',
      notEnoughTitle: 'لا يوجد رصيد كافي',
      notEnoughSub: (missing) => `تحتاج إلى ${missing} عملة إضافية لفتح النسخة المميزة.`,
      unlocking: 'جاري الفتح...',
    },
  },
  boost: {
    icon: Zap,
    accentColor: '#d97706',
    accentBg: '#fffbeb',
    accentBorder: '#fde68a',
    gradientFrom: '#f59e0b',
    gradientTo: '#d97706',
    en: {
      title: 'Boost Your Ad',
      subtitle: 'Get more eyes on your listing and sell faster.',
      benefits: ['Better visibility', 'More reach', 'Higher chance of buyer attention'],
      ctaPrimary: 'Boost Now',
      ctaSecondary: 'Get More Coins',
      successTitle: 'Upgrade successful',
      successSub: 'Your premium feature is now active',
      notEnoughTitle: 'Not enough coins',
      notEnoughSub: (missing) => `You need ${missing} more coins to boost your ad.`,
      unlocking: 'Boosting...',
    },
    ar: {
      title: 'تعزيز إعلانك',
      subtitle: 'احصل على مشاهدات أكثر لإعلانك وبيع أسرع.',
      benefits: ['رؤية أفضل', 'انتشار أوسع', 'فرصة أعلى لجذب المشترين'],
      ctaPrimary: 'تعزيز الآن',
      ctaSecondary: 'احصل على عملات',
      successTitle: 'تم التفعيل بنجاح',
      successSub: 'ميزتك المميزة أصبحت مفعلة الآن',
      notEnoughTitle: 'لا توجد عملات كافية',
      notEnoughSub: (missing) => `تحتاج إلى ${missing} عملة إضافية لتعزيز إعلانك.`,
      unlocking: 'جاري التعزيز...',
    },
  },
};

export default function CoinPurchasePanel({
  variant = 'cv',
  price,
  loading = false,
  success = false,
  onPurchase,
  onClose,
  extra = {},
}) {
  const { i18n } = useTranslation();
  const { user, token, updateCoins } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language?.startsWith('ar');
  const lang = isRTL ? 'ar' : 'en';

  // Live-refresh the canonical balance when a purchase panel opens — a stale
  // auth-context balance must never decide affordability (iter382).
  React.useEffect(() => {
    if (!user || !token || COINS_DISABLED) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/wallet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled && typeof d.balance === 'number') updateCoins(d.balance);
      } catch (_e) { /* keep context value */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, token]);

  if (COINS_DISABLED) return null;

  const cfg = VARIANTS[variant] || VARIANTS.cv;
  const txt = cfg[lang];
  const Icon = cfg.icon;

  const userCoins = user?.coins || 0;
  const hasEnough = userCoins >= price;
  const missing = price - userCoins;

  const goToWallet = () => {
    if (onClose) onClose();
    navigate('/wallet', { state: { from: location.pathname } });
  };

  // ---- SUCCESS STATE ----
  if (success) {
    return (
      <div className="cpp-panel" data-testid="cpp-success">
        <div className="cpp-icon-wrap" style={{ background: cfg.accentBg }}>
          <CheckCircle size={32} style={{ color: cfg.accentColor }} />
        </div>
        <h3 className="cpp-title" style={{ color: cfg.accentColor }}>{txt.successTitle}</h3>
        <p className="cpp-subtitle">{txt.successSub}</p>
      </div>
    );
  }

  // ---- MAIN STATE ----
  return (
    <div className="cpp-panel" data-testid="cpp-panel">
      {/* Icon + Title */}
      <div className="cpp-icon-wrap" style={{ background: cfg.accentBg }}>
        <Icon size={28} style={{ color: cfg.accentColor }} />
      </div>
      <h3 className="cpp-title">{txt.title}</h3>
      <p className="cpp-subtitle">{txt.subtitle}</p>

      {/* Extra context (e.g., ad title for boost) */}
      {extra.adTitle && (
        <p className="cpp-extra-context">"{extra.adTitle.slice(0, 50)}{extra.adTitle.length > 50 ? '...' : ''}"</p>
      )}

      {/* Benefits */}
      <ul className="cpp-benefits">
        {txt.benefits.map((b, i) => (
          <li key={i}><Check size={14} style={{ color: cfg.accentColor }} /> {b}</li>
        ))}
      </ul>

      {/* Cost + Balance */}
      <div className="cpp-cost-card" style={{ background: cfg.accentBg, borderColor: cfg.accentBorder }}>
        <p className="cpp-cost-line">
          {isRTL ? 'السعر:' : 'Price:'}{' '}
          <strong style={{ color: cfg.accentColor }}>{price} {isRTL ? 'عملة' : 'coins'}</strong>
        </p>
        <p className="cpp-cost-line">
          {isRTL ? 'رصيدك:' : 'Your balance:'}{' '}
          <strong style={{ color: hasEnough ? cfg.accentColor : '#dc2626' }}>{userCoins} {isRTL ? 'عملة' : 'coins'}</strong>
        </p>
      </div>

      {/* Actions */}
      {hasEnough ? (
        <div className="cpp-actions">
          <Button
            className="cpp-btn-primary"
            style={{ background: `linear-gradient(135deg, ${cfg.gradientFrom}, ${cfg.gradientTo})` }}
            onClick={onPurchase}
            disabled={loading}
            data-testid="cpp-purchase-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
            {loading ? txt.unlocking : txt.ctaPrimary}
          </Button>
          <Button variant="outline" className="cpp-btn-secondary" onClick={goToWallet} data-testid="cpp-wallet-btn">
            {txt.ctaSecondary}
          </Button>
        </div>
      ) : (
        <div className="cpp-actions">
          <div className="cpp-insufficient" data-testid="cpp-insufficient">
            <AlertCircle size={16} style={{ color: '#dc2626' }} />
            <div>
              <p className="cpp-insuf-title">{txt.notEnoughTitle}</p>
              <p className="cpp-insuf-sub">{txt.notEnoughSub(missing)}</p>
            </div>
          </div>
          <Button
            className="cpp-btn-primary"
            style={{ background: `linear-gradient(135deg, ${cfg.gradientFrom}, ${cfg.gradientTo})` }}
            onClick={goToWallet}
            data-testid="cpp-wallet-btn"
          >
            {txt.ctaSecondary}
          </Button>
        </div>
      )}

      {/* Trust note */}
      <p className="cpp-trust-note">
        {isRTL
          ? 'يتم خصم العملات مرة واحدة فقط. لا يوجد رسوم إضافية.'
          : 'Coins are deducted once only. No hidden charges.'}
      </p>
    </div>
  );
}
